import { motion } from "framer-motion";
import { Reveal } from "@/components/site/Reveal";
import { ProductCard } from "@/components/site/ProductCard";
import { staggerContainer, fadeUp } from "@/lib/motion";
import { TURMERIC, RED_CHILLI, GINGER, WHOLE_SPICES } from "@/data/products";
import { Leaf, Sparkles, Check } from "lucide-react";

const SubHeader = ({ n, title, blurb, tone = "cream" }) => {
  const isDark = tone === "dark";
  return (
    <Reveal className="mb-10 lg:mb-14">
      <div className="flex items-baseline gap-6">
        <span
          className={`font-display text-5xl md:text-6xl leading-none ${
            isDark ? "text-[#E0A96D]/50" : "text-[#7A4E24]/40"
          }`}
        >
          {n}
        </span>
        <h3
          className={`font-display text-3xl md:text-5xl leading-[0.95] tracking-tight ${
            isDark ? "text-[#F0E6C0]" : "text-[#3D2817]"
          }`}
        >
          {title}
        </h3>
      </div>
      {blurb && (
        <p
          className={`mt-4 max-w-3xl text-base md:text-lg leading-relaxed pl-0 md:pl-20 ${
            isDark ? "text-[#E6D9A8]/85" : "text-[#6B4423]"
          }`}
        >
          {blurb}
        </p>
      )}
    </Reveal>
  );
};

const HeroBanner = ({ item, testId }) => (
  <Reveal>
    <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16 items-center">
      <div className="lg:col-span-6">
        <div className="overflow-hidden arch-frame border border-[#F0E6C0]/30">
          {item.image ? (
            <div className="h-80 md:h-[28rem] w-full bg-[#4A3120] flex items-center justify-center p-4">
              <img
                src={item.image}
                alt={item.name}
                loading="lazy"
                className="max-h-full max-w-full object-contain"
              />
            </div>
          ) : (
            <div className="h-80 md:h-[28rem] flex items-center justify-center bg-[#4A3120] relative">
              <div
                className="absolute inset-0 opacity-25"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(45deg, transparent 0 16px, rgba(240,230,192,0.1) 16px 17px)",
                }}
              />
              <div className="relative text-center">
                <Sparkles size={40} strokeWidth={1.2} className="mx-auto text-[#E0A96D]" />
                <p className="mt-4 font-display text-4xl italic text-[#F0E6C0]">
                  {item.name}
                </p>
                <p className="mt-2 text-[10px] uppercase tracking-[0.25em] text-[#D8B98A]">
                  Photo slot
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="lg:col-span-6" data-testid={testId}>
        {item.badge && (
          <span className="inline-block rounded-full border border-[#E0A96D] px-4 py-1 text-[10px] uppercase tracking-[0.2em] text-[#E0A96D]">
            {item.badge}
          </span>
        )}
        <h4 className="mt-5 font-display text-5xl md:text-6xl leading-[0.95] tracking-tight text-[#F0E6C0]">
          {item.name}
        </h4>
        <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-[#E6D9A8]/85">
          {item.description}
        </p>
      </div>
    </div>
  </Reveal>
);

export const Spices = () => (
  <section
    id="spices"
    data-testid="spices-section"
    className="bg-[#3D2817] py-24 lg:py-40 text-[#F0E6C0]"
  >
    <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
      <Reveal className="mb-20 lg:mb-28 max-w-3xl">
        <p className="mb-5 text-xs md:text-sm uppercase tracking-[0.25em] text-[#D8B98A]">
          &#8212; Spices
        </p>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl leading-[0.9] tracking-tight">
          North-East India&apos;s
          <span className="block italic text-[#E0A96D]">most prized botanicals.</span>
        </h2>
      </Reveal>

      {/* Turmeric */}
      <SubHeader
        n="01"
        title="Turmeric"
        blurb={"Meghalaya\u2019s crown jewel Lakadong sets the global benchmark for curcumin content."}
        tone="dark"
      />
      <HeroBanner item={TURMERIC.hero} testId="turmeric-lakadong" />
      <motion.div
        className="mt-16 grid grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-4"
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.05 }}
      >
        {TURMERIC.varieties.map((v) => (
          <ProductCard
            key={v.id}
            testId={`turmeric-${v.id}`}
            name={v.name}
            badge={v.badge}
            image={v.image}
            description={v.description}
            size="sm"
            tone="dark"
          />
        ))}
      </motion.div>
      <p className="mt-10 text-sm uppercase tracking-[0.2em] text-[#D8B98A]">
        Variants: {TURMERIC.variants}
      </p>

      {/* Red Chilli */}
      <div className="mt-28 lg:mt-40">
        <SubHeader
          n="02"
          title="Red Chilli"
          blurb={"From the world\u2019s hottest Ghost Pepper to nuanced regional varieties \u2014 heat, colour, and story."}
          tone="dark"
        />
        <HeroBanner item={RED_CHILLI.hero} testId="chilli-bhut-jolokhia" />
        <motion.div
          className="mt-16 grid grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-4"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.05 }}
        >
          {RED_CHILLI.varieties.map((v) => (
            <ProductCard
              key={v.id}
              testId={`chilli-${v.id}`}
              name={v.name}
              image={v.image}
              description={v.description}
              size="sm"
              tone="dark"
            />
          ))}
        </motion.div>
        <p className="mt-10 text-sm uppercase tracking-[0.2em] text-[#D8B98A]">
          Variants: {RED_CHILLI.variants}
        </p>
      </div>

      {/* Ginger */}
      <div className="mt-28 lg:mt-40" data-testid="ginger-block">
        <SubHeader
          n="03"
          title="Ginger"
          blurb={GINGER.intro}
          tone="dark"
        />
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-7">
            <div className="rounded-2xl border border-[#F0E6C0]/25 overflow-hidden">
              {GINGER.image ? (
                <div className="h-96 w-full bg-[#4A3120] flex items-center justify-center p-4">
                  <img
                    src={GINGER.image}
                    alt="North-East India Ginger"
                    loading="lazy"
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
              ) : (
                <div className="h-96 flex items-center justify-center bg-[#4A3120] relative">
                  <div
                    className="absolute inset-0 opacity-25"
                    style={{
                      backgroundImage:
                        "repeating-linear-gradient(45deg, transparent 0 16px, rgba(240,230,192,0.1) 16px 17px)",
                    }}
                  />
                  <div className="relative text-center">
                    <Leaf size={38} strokeWidth={1.2} className="mx-auto text-[#E0A96D]" />
                    <p className="mt-4 font-display text-3xl italic text-[#F0E6C0]">Ginger</p>
                    <p className="mt-2 text-[10px] uppercase tracking-[0.25em] text-[#D8B98A]">
                      Photo slot (regional ginger)
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.2em] text-[#D8B98A]">Why Our Ginger Stands Out</p>
            <ul className="mt-4 space-y-3">
              {GINGER.strengths.map((s) => (
                <li key={s} className="flex items-start gap-3">
                  <Check size={18} className="mt-1 shrink-0 text-[#E0A96D]" />
                  <span className="text-base text-[#F0E6C0]">{s}</span>
                </li>
              ))}
            </ul>
            <p className="mt-8 text-xs uppercase tracking-[0.2em] text-[#D8B98A]">Regional Varieties</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {GINGER.regions.map((r) => (
                <span
                  key={r}
                  className="rounded-full border border-[#F0E6C0]/30 px-3 py-1 text-xs text-[#E6D9A8]"
                >
                  {r}
                </span>
              ))}
            </div>
            <p className="mt-6 text-xs uppercase tracking-[0.2em] text-[#D8B98A]">Format Variants</p>
            <p className="mt-2 text-sm text-[#E6D9A8]/85">{GINGER.variants}</p>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <ProductCard
              testId="ginger-black-ginger"
              name={GINGER.blackGinger.name}
              image={GINGER.blackGinger.image}
              description={GINGER.blackGinger.description}
              size="md"
              tone="dark"
            />
          </div>
        </div>
      </div>

      {/* Whole spices */}
      <div className="mt-28 lg:mt-40">
        <SubHeader
          n="04"
          title="Whole Spices"
          blurb={"Bayleaf, cinnamon, cardamom, black pepper, star anise \u2014 sourced for aroma, purity, and precision."}
          tone="dark"
        />
        <motion.div
          className="grid grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-5"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.05 }}
        >
          {WHOLE_SPICES.map((s) => (
            <ProductCard
              key={s.id}
              testId={`whole-spice-${s.id}`}
              name={s.name}
              image={s.image}
              description={s.description}
              size="sm"
              tone="dark"
            />
          ))}
        </motion.div>
      </div>
    </div>
  </section>
);
